import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { protocolsI18n } from './i18n';
import { getCallSpecific } from '../../services/protocolService';
import './protocolGeneralDetail.scss';

const VerCallDetailModal = ({
  Modal,
  Table,
  show,
  callId,
  onClose,
  onOpenDetail,
}) => {
  const { t } = useTranslation(undefined, { i18n: protocolsI18n });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizeCallSpecific = (payload) => {
    if (!payload) return null;
    if (payload.objectiveJson) return payload;
    if (payload.data?.objectiveJson) return payload.data;
    if (payload.data?.data?.objectiveJson) return payload.data.data;
    return payload;
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadDetail = async () => {
      if (!show || !callId) {
        setDetail(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getCallSpecific(callId, controller.signal);
        setDetail(normalizeCallSpecific(response));
      } catch (fetchError) {
        console.error('Error loading call detail:', fetchError);
        setError(t('pr_call_modal_error'));
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();

    return () => controller.abort();
  }, [show, callId, t]);

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    const asNumber = Number(value);
    if (Number.isNaN(asNumber)) return `${value}%`;
    return `${Math.floor(asNumber)}%`;
  };

  const objective = detail?.objectiveJson;
  const steps = Array.isArray(objective?.steps) ? objective.steps : [];

  const columns = [
    { accessor: 'stepName', label: t('pr_call_modal_col_step') },
    {
      accessor: 'compliancePercent',
      label: t('pr_call_modal_col_result'),
      render: (row) => formatPercent(row.compliancePercent),
    },
    {
      accessor: 'explanation',
      label: t('pr_call_modal_col_detail'),
      render: (row) => row.explanation || '-',
    },
  ];

  if (!Modal || !Table) return null;

  const titleContent = (
    <div className='d-flex align-items-center gap-2'>
      <span>{t('pr_call_modal_title')}</span>
      {onOpenDetail && callId && (
        <button
          type='button'
          className='btn btn-link p-0'
          onClick={() => onOpenDetail(callId)}
        >
          {t('pr_call_modal_btn_full_detail')}
        </button>
      )}
    </div>
  );

  return (
    <Modal
      title={titleContent}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('pr_call_modal_btn_close')}
      primaryBtnAction={onClose}
      secondaryBtnTxt={undefined}
      width={900}
    >
      <div className='p-3'>
        {loading && (
          <p className='color-gray-600 small mb-0'>{t('pr_call_modal_loading')}</p>
        )}
        {error && <p className='text-danger small mb-0'>{error}</p>}
        {!loading && !error && (
          <div className='d-flex flex-column gap-3'>
            <div>
              <span className='fw-semibold'>{t('pr_call_modal_overall')}</span>{' '}
              {formatPercent(objective?.overallCompliancePercent)}
            </div>
            <div className='table-container detalle-llamada-table'>
              <Table columns={columns} data={steps} />
            </div>
            {steps.length === 0 && (
              <p className='caption font-light mb-0'>
                {t('pr_call_modal_no_detail')}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VerCallDetailModal;
